import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import LineItemTable from "../shared/LineItemTable";
import StatusBadge from "../shared/StatusBadge";
import "./InvoiceBuilder.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const BLANK_ITEM = { service_id: "", description: "", qty: 1, unit_price: 0 };

const defaultDueDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split("T")[0];
};

// API may return a plain date ("2026-08-21") or a full ISO timestamp
// ("2026-08-21T00:00:00.000000Z") — normalize to just the date part so it
// works both as a display string and as an <input type="date"> value.
const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

const formatDate = (value) => {
    const dateOnly = toDateOnly(value);
    if (!dateOnly) return "";
    const [year, month, day] = dateOnly.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-CA");
};

const InvoiceBuilder = ({ readOnly = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const printRef = useRef();

    const [clients, setClients] = useState([]);
    const [services, setServices] = useState([]);
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState({
        client_id: "",
        project_id: "",
        due_date: defaultDueDate(),
        notes: "",
        status: "draft",
    });
    const [items, setItems] = useState([{ ...BLANK_ITEM }]);
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState("");

    useEffect(() => {
        if (!readOnly) {
            Promise.all([api.allClients(), api.services()]).then(
                ([cRes, sRes]) => {
                    setClients(cRes.data || []);
                    setServices(sRes.services || []);
                },
            );
        }
        if (isEdit) {
            api.invoice(id)
                .then(({ invoice: inv }) => {
                    setInvoice(inv);
                    setForm({
                        client_id: inv.client_id,
                        project_id: inv.project_id || "",
                        due_date: toDateOnly(inv.due_date) || defaultDueDate(),
                        notes: inv.notes || "",
                        status: inv.status,
                    });
                    setItems(
                        inv.line_items?.map((li) => ({
                            id: li.id,
                            service_id: li.service_id || "",
                            description: li.description,
                            qty: parseFloat(li.qty),
                            unit_price: parseFloat(li.unit_price),
                        })) || [{ ...BLANK_ITEM }],
                    );
                })
                .catch(() => setError(t("common.error")))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, readOnly, t]);

    // Load the selected client's projects so the invoice can be attached
    // to one (fixes invoices created without a project link).
    useEffect(() => {
        if (readOnly || !form.client_id) {
            setProjects([]);
            return;
        }
        api.clientProjects(form.client_id)
            .then((res) => setProjects(res.projects || []))
            .catch(() => setProjects([]));
    }, [form.client_id, readOnly]);

    const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);

    const updateItem = (index, changes) =>
        setItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, ...changes } : item,
            ),
        );
    const addItem = () => setItems((prev) => [...prev, { ...BLANK_ITEM }]);
    const removeItem = (index) =>
        setItems((prev) => prev.filter((_, i) => i !== index));
    const handleFormChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                project_id: form.project_id || null,
                tax_rate: 0,
                line_items: items.map((i) => ({
                    service_id: i.service_id || null,
                    description: i.description,
                    qty: i.qty,
                    unit_price: i.unit_price,
                })),
            };
            if (isEdit) await api.updateInvoice(id, payload);
            else await api.createInvoice(payload);
            navigate("/admin/invoices");
        } catch (err) {
            setError(err.message || t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleMarkPaid = async () => {
        try {
            await api.updateInvoice(id, { status: "paid" });
            setForm((p) => ({ ...p, status: "paid" }));
            setInvoice((p) => (p ? { ...p, status: "paid" } : p));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSendEmail = async () => {
        const already = invoice?.sent_at;
        if (
            already &&
            !window.confirm(
                `This invoice was already emailed on ${new Date(already).toLocaleDateString("en-CA")}. Send it again?`,
            )
        )
            return;
        setSending(true);
        setError("");
        setSendSuccess("");
        try {
            const res = await api.sendInvoiceEmail(id);
            setInvoice(res.invoice);
            setForm((p) => ({ ...p, status: res.invoice.status }));
            setSendSuccess(res.message || "Invoice sent.");
        } catch (err) {
            setError(err.message || t("common.error"));
        } finally {
            setSending(false);
        }
    };

    if (loading)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                {t("common.loading")}
            </p>
        );

    const isPrintView = readOnly;
    const clientName =
        invoice?.client?.name ||
        clients.find((c) => String(c.id) === String(form.client_id))?.name ||
        "";

    return (
        <div
            className={`invoice-builder ${isPrintView ? "invoice-builder--print" : ""}`}
            ref={printRef}
        >
            {isPrintView && (
                <div className="invoice-builder__view-toolbar no-print">
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => navigate("/dashboard/invoices")}
                    >
                        ← Back
                    </button>
                    <button
                        className="btn btn--primary btn--sm"
                        onClick={() => window.print()}
                    >
                        {t("invoice.print")}
                    </button>
                </div>
            )}

            {isPrintView && (
                <div className="invoice-print">
                    <div className="invoice-print__accent-bar" />
                    <div className="invoice-print__header">
                        <div className="invoice-print__brand">
                            <div className="invoice-print__logo">KS</div>
                            <div className="invoice-print__from">
                                <strong>Kyle Street</strong>
                                <span>Web Development &amp; IT Services</span>
                                <span>admin@kyle-street.com</span>
                                <span>kyle-street.com</span>
                            </div>
                        </div>
                        <div className="invoice-print__doc-title">
                            <h1>{t("invoice.title")}</h1>
                            <div className="invoice-print__number">
                                {invoice?.invoice_number}
                            </div>
                            <StatusBadge status={invoice?.status} />
                        </div>
                    </div>

                    <div className="invoice-print__parties">
                        <div className="invoice-print__party">
                            <span className="invoice-print__party-label">
                                {t("invoice.client")}
                            </span>
                            <strong>{clientName}</strong>
                            {invoice?.client?.company && (
                                <span>{invoice.client.company}</span>
                            )}
                            {invoice?.client?.email && (
                                <span>{invoice.client.email}</span>
                            )}
                            {invoice?.client?.phone && (
                                <span>{invoice.client.phone}</span>
                            )}
                        </div>
                        <table className="invoice-print__meta-table">
                            <tbody>
                                <tr>
                                    <td>{t("invoice.dueDate")}</td>
                                    <td>{formatDate(invoice?.due_date)}</td>
                                </tr>
                                <tr>
                                    <td>Issued</td>
                                    <td>
                                        {invoice?.created_at
                                            ? new Date(
                                                  invoice.created_at,
                                              ).toLocaleDateString("en-CA")
                                            : ""}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!isPrintView && (
                <div className="invoice-builder__toolbar">
                    <div className="section-header">
                        <h2>{isEdit ? t("invoice.edit") : t("invoice.new")}</h2>
                    </div>
                    <div className="invoice-builder__toolbar-actions">
                        {isEdit && invoice && (
                            <>
                                <StatusBadge status={form.status} />
                                {form.status !== "paid" && (
                                    <button
                                        className="btn btn--ghost btn--sm"
                                        onClick={handleMarkPaid}
                                    >
                                        {t("invoice.markPaid")}
                                    </button>
                                )}
                                <button
                                    className="btn btn--ghost btn--sm no-print"
                                    onClick={handleSendEmail}
                                    disabled={sending}
                                    title={
                                        invoice?.sent_at
                                            ? `Last sent ${new Date(invoice.sent_at).toLocaleDateString("en-CA")}`
                                            : "Email this invoice to the client"
                                    }
                                >
                                    {sending
                                        ? "Sending…"
                                        : invoice?.sent_at
                                          ? t("invoice.resendEmail")
                                          : t("invoice.sendEmail")}
                                </button>
                                <button
                                    className="btn btn--ghost btn--sm no-print"
                                    onClick={() =>
                                        navigate(`/dashboard/invoices/${id}`)
                                    }
                                >
                                    {t("invoice.print")}
                                </button>
                                {form.project_id && (
                                    <button
                                        className="btn btn--ghost btn--sm no-print"
                                        onClick={() =>
                                            navigate(
                                                `/admin/projects/${form.project_id}`,
                                            )
                                        }
                                    >
                                        View Project
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {error && <div className="error-message">{error}</div>}
            {sendSuccess && (
                <div className="success-message no-print">{sendSuccess}</div>
            )}

            <form onSubmit={handleSave}>
                {!isPrintView && (
                    <div className="card invoice-builder__meta">
                        <div className="invoice-builder__meta-grid">
                            <div className="form-group">
                                <label>{t("invoice.client")}</label>
                                <select
                                    name="client_id"
                                    value={form.client_id}
                                    onChange={handleFormChange}
                                    required
                                    disabled={isEdit}
                                >
                                    <option value="">— Select client —</option>
                                    {clients
                                        .filter((c) => c.role === "client")
                                        .map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Attach to Project (optional)</label>
                                <select
                                    name="project_id"
                                    value={form.project_id}
                                    onChange={handleFormChange}
                                    disabled={!form.client_id}
                                >
                                    <option value="">— None —</option>
                                    {projects.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t("invoice.dueDate")}</label>
                                <input
                                    type="date"
                                    name="due_date"
                                    value={form.due_date}
                                    onChange={handleFormChange}
                                    required
                                />
                            </div>
                            {isEdit && (
                                <div className="form-group">
                                    <label>{t("invoice.status")}</label>
                                    <select
                                        name="status"
                                        value={form.status}
                                        onChange={handleFormChange}
                                    >
                                        {[
                                            "draft",
                                            "sent",
                                            "paid",
                                            "overdue",
                                        ].map((s) => (
                                            <option key={s} value={s}>
                                                {t(`status.${s}`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label>{t("invoice.notes")}</label>
                            <textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleFormChange}
                                placeholder="Payment terms, notes for client…"
                            />
                        </div>
                    </div>
                )}

                <div
                    className={isPrintView ? "" : "card invoice-builder__items"}
                >
                    {!isPrintView && (
                        <div className="section-header">
                            <h3>Line Items</h3>
                        </div>
                    )}
                    <LineItemTable
                        items={items}
                        editable={!isPrintView}
                        services={services}
                        onItemChange={updateItem}
                        onAddItem={addItem}
                        onRemoveItem={removeItem}
                    />

                    <div className="invoice-builder__totals">
                        {isPrintView ? (
                            <>
                                <div className="invoice-builder__total-row">
                                    <span>{t("totals.subtotal")}</span>
                                    <span>{formatCAD(invoice?.subtotal)}</span>
                                </div>
                                {parseFloat(invoice?.tax_amount) > 0 && (
                                    <div className="invoice-builder__total-row">
                                        <span>{t("totals.hst")}</span>
                                        <span>
                                            {formatCAD(invoice?.tax_amount)}
                                        </span>
                                    </div>
                                )}
                                <div className="invoice-builder__total-row invoice-builder__total-row--grand">
                                    <span>{t("totals.total")}</span>
                                    <span>{formatCAD(invoice?.total)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="invoice-builder__total-row invoice-builder__total-row--grand">
                                <span>{t("totals.total")}</span>
                                <span>{formatCAD(subtotal)}</span>
                            </div>
                        )}
                    </div>

                    {form.notes && (
                        <p className="invoice-builder__notes">{form.notes}</p>
                    )}

                    {isPrintView && (
                        <div className="invoice-print__payment">
                            <span className="invoice-print__party-label">
                                {t("invoice.paymentMethods")}
                            </span>
                            <ul>
                                <li>
                                    <strong>
                                        {t("invoice.paymentInPerson")}
                                    </strong>
                                </li>
                                <li>
                                    <strong>
                                        {t("invoice.paymentETransfer")}
                                    </strong>{" "}
                                    — {t("invoice.paymentETransferNote")}{" "}
                                    <span className="invoice-print__payment-email">
                                        kyle.street@gmail.com
                                    </span>
                                </li>
                                <li>
                                    <strong>{t("invoice.paymentOther")}</strong>{" "}
                                    — {t("invoice.paymentOtherNote")}
                                </li>
                            </ul>
                        </div>
                    )}

                    {isPrintView && (
                        <div className="invoice-print__footer">
                            <p className="invoice-print__thanks">
                                Thank you for your business.
                            </p>
                            <p>
                                Please make payment by the due date above. If
                                you have any questions about this invoice, reply
                                to this email or reach out via the client
                                portal.
                            </p>
                        </div>
                    )}
                </div>

                {isPrintView &&
                    ["paid", "overdue"].includes(invoice?.status) && (
                        <div
                            className={`invoice-print__stamp invoice-print__stamp--${invoice.status}`}
                        >
                            {t(`status.${invoice.status}`)}
                        </div>
                    )}

                {!isPrintView && (
                    <div className="invoice-builder__footer">
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => navigate("/admin/invoices")}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="btn btn--primary"
                            disabled={saving}
                        >
                            {saving ? t("common.loading") : t("common.save")}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default InvoiceBuilder;
